<div class="container-fluid d-flex flex-fill flex-column gap-7">
    <div class="row">
        <div class="col-12 d-flex justify-content-between align-items-center px-0">
            <span class="heading-4 fw-semi-bold">LinkedIn</span>
        </div>
    </div>
    <div class="row">
        <div class="col-12 px-0">
            <span class="fs-7">Customise how your post will appear on LinkedIn.</span>
        </div>
    </div>
    <div class="row">
        <div class="col-12 col-md-7 d-flex flex-column gap-7 ps-0">
            <div class="d-flex flex-column gap-6">
                <pp-text-area id="liPostTitle"
                    [form]="form"
                    placeholder="Title (optional)"
                    [rows]="2" />
                <pp-text-area id="liPostDescription"
                    [form]="form"
                    placeholder="Description (required)"
                    [rows]="6" />
                <pp-checkbox [checked]="$safeNavigationMigration(form.get('liIncludeOddrLink')?.value)"
                    id="liIncludeOddrLink"
                    label="Include the ODDR verified link in the post description"
                    (checkChanged)="oddrLinkSelected($event)" />
            </div>
            @if (organizations.length > 0) {
                <ng-container [ngTemplateOutlet]="mediaUploadPostAs" />
            }
            @else if (isTeamContext) {
                <!-- The three messages below all fall back to "your personal
                     profile", which is the wrong answer for a business account:
                     that profile belongs to the member who connected LinkedIn.
                     A team with no Page cannot post to LinkedIn at all, and the
                     required liPostAs control keeps the step from being
                     submitted while that is true. -->
                <pp-alert [type]="alertType.Warning"
                    message="This business account has no LinkedIn Company Page available, so it cannot post to LinkedIn yet. A Super admin of the Page needs to reconnect LinkedIn from this account's Integrations and approve Page access." />
            }
            @else if (organizationsUnavailable) {
                <pp-alert [type]="alertType.Warning"
                    message="We couldn't load your LinkedIn Pages, so this post can only go out from your personal profile. Disconnect and reconnect LinkedIn in Integrations to grant Page access." />
            }
            @else if (!pageAccessGranted) {
                <pp-alert [type]="alertType.Warning"
                    message="LinkedIn hasn't granted ODDR permission to see your Company Pages, so this post will go out from your personal profile. Remove ODDR under LinkedIn's Settings &rarr; Data privacy &rarr; Permitted services, then reconnect LinkedIn to approve Page access." />
            }
            @else {
                <pp-alert [type]="alertType.Transparent"
                    message="No LinkedIn Pages found, so this post will go out from your personal profile. LinkedIn only shares Pages where you are a Super admin." />
            }
            @if (!isPostingAsPage) {
                <ng-container [ngTemplateOutlet]="mediaUploadVisibility" />
            }
        </div>
        <div class="col-12 col-md-5 mt-8 mt-md-0 d-flex justify-content-center justify-content-md-start">
            <pp-post-preview provider="linkedin"
                [title]="$safeNavigationMigration(form.get('liPostTitle')?.value)"
                [caption]="$safeNavigationMigration(form.get('liPostDescription')?.value)"
                [includeOddrLink]="$safeNavigationMigration(form.get('liIncludeOddrLink')?.value)"
                [oddrLink]="previewOddrLink"
                [thumbnailUrl]="previewThumbnailUrl"
                [mediaType]="previewMediaType"
                [imageCount]="previewImageCount" />
        </div>
    </div>
</div>
<div class="d-flex justify-content-between align-items-center mt-10">
    <ng-container *ngTemplateOutlet="uploadStatusIndicator" />
    <pp-button [disabled]="form.invalid"
        [shape]="buttonShape.Square"
        [variant]="buttonVariant.Primary"
        (buttonClick)="submitAndGoToNextStep()">Next</pp-button>
</div>

<ng-template #uploadStatusIndicator>
    @if (uploadProgress > 0 && !uploadComplete()) {
        <div class="upload-progress-inline d-flex align-items-center gap-6">
            <span class="fs-6 fw-semi-bold text-nowrap">Uploading... {{ uploadProgress }}%</span>
            <div class="upload-progress-bar-track">
                <div class="upload-progress-bar-fill" [style.width.%]="uploadProgress"></div>
            </div>
        </div>
    } @else if (uploadComplete() && !processingComplete()) {
        <div class="d-flex align-items-center gap-3">
            <span class="fs-6 fw-semi-bold text-nowrap">Processing...</span>
        </div>
    } @else if (processingComplete()) {
        <div class="d-flex align-items-center gap-3">
            <img alt="Ready" src="/assets/images/icons/check-circle-solid-primary.svg" height="24px" width="24px" />
            <span class="fs-6 fw-semi-bold text-nowrap">Ready</span>
        </div>
    } @else {
        <div></div>
    }
</ng-template>

<ng-template #mediaUploadPostAs>
    <div class="card">
        <div class="card-body d-flex flex-column gap-7">
            <span class="fs-6 fw-semi-bold">Post as</span>
            @if (isTeamContext) {
                <span class="fs-6">Choose the Company Page that publishes this post.</span>
            }
            @else {
                <span class="fs-6">Publish to your personal profile, or as a Company Page you manage.</span>
            }
            <!-- Never offered on a business account: the personal profile is the
                 member who connected LinkedIn, not the business, so posting there
                 would publish the business's media to an individual's own feed. -->
            @if (!isTeamContext) {
                <pp-radio-button [checked]="!isPostingAsPage"
                    id="liPostAsProfile"
                    label="Your profile"
                    name="mediaUploadLiPostAsSelection"
                    text="The post appears on your personal LinkedIn profile."
                    value=""
                    (optionChanged)="postAsSelected($event)" />
            }
            @for (org of organizations; track org.id) {
                <pp-radio-button [checked]="form.get('liPostAs')?.value === org.id"
                    [id]="org.id"
                    [label]="org.name"
                    name="mediaUploadLiPostAsSelection"
                    text="The post is published by this Company Page (public)."
                    [value]="org.id"
                    (optionChanged)="postAsSelected($event)" />
            }
        </div>
    </div>
</ng-template>

<ng-template #mediaUploadVisibility>
    <div class="card">
        <div class="card-body d-flex flex-column gap-7">
            <span class="fs-6 fw-semi-bold">Visibility</span>
            <span class="fs-6">Make your post public or only available only to your connections.</span>
            <pp-radio-button [checked]="form.get('liPostVisibility')?.value === visibility.Connections"
                id="Connections"
                label="Connections"
                name="mediaUploadLiVisibilitySelection"
                text="Only your connctions can see your post."
                [value]="visibility.Connections"
                (optionChanged)="postVisibilitySelected($event)" />
            <pp-radio-button [checked]="form.get('liPostVisibility')?.value === visibility.Public"
                id="Public"
                label="Public"
                name="mediaUploadLiVisibilitySelection"
                text="Everyone can see your post."
                [value]="visibility.Public"
                (optionChanged)="postVisibilitySelected($event)" />
        </div>
    </div>
</ng-template>
